import type { AuditResponse } from '../api/client';
import { formatDate, shortHash } from '../lib/format';

/**
 * Audit viewer.
 *
 * Renders a pass/fail badge derived from `chain_valid` + the full event
 * table. Component test (`AuditViewer.test.tsx`) asserts both the
 * `chain_valid: true` and `chain_valid: false` paths surface the correct
 * badge text and class.
 */
export function AuditViewer({ data }: { data: AuditResponse }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="font-medium">Chain status:</span>
        {data.chain_valid ? (
          <span
            data-testid="chain-badge"
            className="badge bg-emerald-100 text-emerald-700"
          >
            valid
          </span>
        ) : (
          <span
            data-testid="chain-badge"
            className="badge bg-red-100 text-red-700"
          >
            invalid
            {data.first_invalid_seq !== null
              ? ` (first invalid seq: ${data.first_invalid_seq})`
              : null}
          </span>
        )}
      </div>
      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase text-slate-500">
          <tr>
            <th className="px-2 py-1">#</th>
            <th className="px-2 py-1">Event</th>
            <th className="px-2 py-1">Actor</th>
            <th className="px-2 py-1">When</th>
            <th className="px-2 py-1">Hash</th>
          </tr>
        </thead>
        <tbody>
          {data.events.map((e) => (
            <tr key={e.id} className="border-t border-slate-100">
              <td className="px-2 py-1 font-mono">{e.seq}</td>
              <td className="px-2 py-1">{e.event_type}</td>
              <td className="px-2 py-1 text-slate-600">
                {e.actor_type ?? '—'}
                {e.actor_id ? `:${e.actor_id.slice(0, 8)}` : ''}
              </td>
              <td className="px-2 py-1 text-slate-600">
                {formatDate(e.occurred_at)}
              </td>
              <td className="px-2 py-1 font-mono text-xs text-slate-500">
                {shortHash(e.event_hash)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
